import { motion } from "framer-motion";
import { useId, cloneElement, isValidElement } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, Play } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { API } from "@/lib/api";
import { ytThumb, imageUrl } from "@/lib/images";

export const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, className = "", ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, ease: EASE, delay }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function SectionHeading({ eyebrow, title, sub, dark = false, center = false }) {
  return (
    <Reveal className={`mb-12 max-w-3xl ${center ? "mx-auto text-center" : ""}`}>
      {eyebrow && (
        <p className={`mb-3 text-xs font-bold uppercase tracking-[0.24em] ${dark ? "text-gold" : "text-gold-hover"}`} data-testid="section-eyebrow">
          {eyebrow}
        </p>
      )}
      <h2 className={`font-display text-3xl font-semibold leading-[1.15] tracking-tight sm:text-4xl ${dark ? "text-white" : "text-navy"}`}>
        {title}
      </h2>
      {sub && <p className={`mt-4 text-base leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{sub}</p>}
      <div className={`mt-6 h-[3px] w-16 bg-gold ${center ? "mx-auto" : ""}`} />
    </Reveal>
  );
}

export function Marquee() {
  const { t } = useLanguage();
  const items = [1, 2, 3, 4, 5, 6].map((i) => t(`marquee.${i}`));
  const row = [...items, ...items, ...items];
  return (
    <div data-testid="editorial-marquee" className="overflow-hidden border-y border-gold/20 bg-navy py-4" dir="ltr">
      <div className="marquee-track">
        {[0, 1].map((half) => (
          <div key={half} className="flex shrink-0 items-center">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center whitespace-nowrap font-display text-sm tracking-[0.3em] text-gold-light/80">
                <span className="px-8 uppercase">{item}</span>
                <span className="text-gold">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Field({ label, required, children, hint, testid }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div>
      <label htmlFor={id} className="field-label" data-testid={testid ? `${testid}-label` : undefined}>
        {label} {required && <span className="text-gold-hover" aria-hidden="true">*</span>}
      </label>
      {isValidElement(children)
        ? cloneElement(children, {
            id,
            "aria-required": required || undefined,
            "aria-describedby": hintId,
          })
        : children}
      {hint && <p id={hintId} className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export const inputCls = "field-input";

export function PriceTag({ currency, fee, discount, large = false }) {
  if (!fee && !discount) return null;
  const hasDiscount = discount != null && fee != null && discount > 0 && discount < fee;
  const shown = hasDiscount ? discount : fee;
  return (
    <span className={`font-bold text-navy ${large ? "text-2xl font-display" : "text-sm"}`} data-testid="price-tag">
      {hasDiscount && (
        <span className={`me-2 font-normal text-slate-400 line-through ${large ? "text-base" : "text-xs"}`} data-testid="price-original">
          {currency} {Number(fee).toLocaleString()}
        </span>
      )}
      {currency} {Number(shown).toLocaleString()}
    </span>
  );
}

export function CourseCard({ course }) {
  const { t, pick, lang } = useLanguage();
  return (
    <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.25 }}>
      <Link
        to={`/training/${course.id}`}
        data-testid={`course-card-${course.id}`}
        className="group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
      >
        <div className="relative h-44 overflow-hidden">
          <img
            src={imageUrl(course.featured_image)}
            alt={pick(course, "title")}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <span className="absolute start-3 top-3 rounded-full bg-navy/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-light">
            {course.category}
          </span>
          {course.featured && (
            <span className="absolute end-3 top-3 rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-navy">
              {t("common.featured")}
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col p-5">
          <h3 className={`font-display text-xl font-semibold leading-snug text-navy ${lang === "ar" ? "" : ""}`}>{pick(course, "title")}</h3>
          <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-600">{pick(course, "short_description")}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            {course.duration && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-gold" />{course.duration}</span>}
            {course.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-gold" />{course.location}</span>}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
            {course.course_state && course.course_state !== "open" ? (
              <>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500" data-testid={`course-state-${course.course_state}`}>
                  {t(course.course_state === "completed" ? "course.completed" : "course.closed")}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`course-request-${course.id}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = "/training?request=1"; }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); window.location.href = "/training?request=1"; } }}
                  className="cursor-pointer text-xs font-bold uppercase tracking-wider text-gold-hover"
                >
                  {t("course.requestUpcoming")} →
                </span>
              </>
            ) : (
              <>
                <PriceTag currency={course.currency} fee={course.fee} discount={course.discount_price} />
                <span className="text-xs font-bold uppercase tracking-wider text-gold-hover">{t("training.details")} →</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function ExpertCard({ expert }) {
  const { t } = useLanguage();
  const photo = expert.photo_url || (expert.photo_path ? `${API}/expert-photo/${expert.id}` : null);
  return (
    <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.25 }}>
      <Link
        to={`/experts/${expert.id}`}
        data-testid={`expert-card-${expert.id}`}
        className="group block h-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
          {photo ? (
            <img src={photo} alt={expert.full_name} className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-navy font-display text-4xl text-gold">
              {expert.full_name?.charAt(0)}
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-display text-xl font-semibold text-navy">{expert.full_name}</h3>
          <p className="mt-1 text-sm text-slate-600">{expert.job_title}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {expert.specialization && (
              <span className="rounded-full bg-gold-soft px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-hover">
                {expert.specialization}
              </span>
            )}
            {expert.country && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                {expert.country}
              </span>
            )}
          </div>
          {expert.years_experience > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              {expert.years_experience}+ {t("experts.years")}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

export function InsightCard({ article }) {
  const { t, pick } = useLanguage();
  return (
    <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.25 }}>
      <Link
        to={`/insights/${article.slug}`}
        data-testid={`insight-card-${article.slug}`}
        className="group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
      >
        <div className="h-44 overflow-hidden">
          <img src={article.featured_image} alt={pick(article, "title")} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        </div>
        <div className="flex flex-1 flex-col p-5">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-hover">{article.category}</span>
          <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-navy">{pick(article, "title")}</h3>
          <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-600">{pick(article, "summary")}</p>
          <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
            <span>{article.publish_date}</span>
            <span className="font-bold uppercase tracking-wider text-gold-hover">{t("insights.readMore")} →</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function VideoCard({ video, onPlay }) {
  const { pick } = useLanguage();
  const thumb = video.thumbnail || ytThumb(video.youtube_url);
  return (
    <motion.button
      whileHover={{ y: -5 }}
      transition={{ duration: 0.25 }}
      onClick={() => onPlay(video)}
      data-testid={`video-card-${video.id}`}
      className="group block h-full w-full overflow-hidden rounded-lg border border-slate-200 bg-white text-start shadow-sm transition-shadow hover:shadow-xl"
    >
      <div className="relative h-44 overflow-hidden bg-navy">
        {thumb && <img src={thumb} alt={pick(video, "title")} className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold text-navy shadow-lg transition-transform group-hover:scale-110">
            <Play className="ms-0.5 h-6 w-6" fill="currentColor" />
          </span>
        </span>
      </div>
      <div className="p-5">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-hover">{video.category}</span>
        <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-navy">{pick(video, "title")}</h3>
        {video.speaker && <p className="mt-1 text-xs text-slate-500">{video.speaker}</p>}
      </div>
    </motion.button>
  );
}
